// Territory rules (ports of AppStore.assignRecordsToTerritories / territory(containing:) / records(in:) /
// territoryStats, GroundTeam/Store/AppStore.swift:520-552, Territory.contains, and TerritoryFormat.area in
// Territories+Store.swift). WEB_PLAN.md §5.8.
//
// A store's territory: the first ACTIVE territory whose polygon contains its coordinate, else the first active one
// whose ZIP list has its trimmed postal code, else none. iOS takes "first" in its local array order, which can differ
// between devices; the console orders territories by `createdAt`, then id, so overlapping territories get one stable
// answer. iPhones do not rerun the rule when a territory arrives from the server, and the server's rep visibility
// depends on `territoryId`, so the console pushes every store whose territory changes after a territory edit.

import type { DistanceUnit } from '@/contracts/enums';
import type { ActivityPayload, PipelinePayload, RecordPayload, TerritoryPayload } from '@/contracts/models';
import { addDays } from './calendar';
import { effectiveMs } from './derived';
import { formatFixed } from './format';
import { areaSquareMeters, polygonContains, type Coordinate } from './geo';
import { stageFor } from './pipelines';
import { compareScalars, hasId, idKey, idSet, ms, sameId, trimSpaces } from './util';
import { patch, type Patch } from '@/sync/patch';

/** `Territory.contains(_:)`: ray casting over the polygon (fewer than 3 points contain nothing). */
export function territoryContains(t: Pick<TerritoryPayload, 'polygon'>, c: Coordinate | null | undefined): boolean {
  return !!c && polygonContains(t.polygon, c);
}

/** `Territory.contains(postalCode:)`: exact match of the trimmed code against the stored list. */
export function territoryContainsPostalCode(t: Pick<TerritoryPayload, 'postalCodes'>, postalCode: string): boolean {
  const code = trimSpaces(postalCode);
  return t.postalCodes.includes(code);
}

/** The console's stable territory order: `createdAt` ascending, then id (case-insensitive). */
export function compareTerritories(a: TerritoryPayload, b: TerritoryPayload): number {
  const ta = ms(a.createdAt) ?? 0;
  const tb = ms(b.createdAt) ?? 0;
  return ta - tb || compareScalars(idKey(a.id), idKey(b.id));
}

/** The active territories in the stable order the assignment rule walks. */
export function assignmentOrder(territories: readonly TerritoryPayload[]): TerritoryPayload[] {
  return territories.filter((t) => t.isActive).sort(compareTerritories);
}

function territoryForRecordOrdered(
  record: Pick<RecordPayload, 'coordinate' | 'address'>,
  ordered: readonly TerritoryPayload[],
): TerritoryPayload | undefined {
  if (record.coordinate) {
    const byShape = ordered.find((t) => polygonContains(t.polygon, record.coordinate!));
    if (byShape) return byShape;
  }
  return ordered.find((t) => territoryContainsPostalCode(t, record.address.postalCode));
}

/** The territory a store belongs to under the assignment rule (polygon first, then ZIP; active only). */
export function territoryForRecord(
  record: Pick<RecordPayload, 'coordinate' | 'address'>,
  territories: readonly TerritoryPayload[],
): TerritoryPayload | undefined {
  return territoryForRecordOrdered(record, assignmentOrder(territories));
}

/**
 * `assignRecordsToTerritories()` as patches: one `records` update per store (archived ones included, as iOS) whose
 * `territoryId` differs from the rule's answer; the key is removed when no territory matches. Pass the territory list
 * as it will be after the edit (the edited territory replaced, a deleted one left out), and put these patches in the
 * same `save` as the territory change.
 */
export function territoryAssignmentPatches(
  records: readonly RecordPayload[],
  territories: readonly TerritoryPayload[],
): Patch[] {
  const ordered = assignmentOrder(territories);
  const out: Patch[] = [];
  for (const r of records) {
    const t = territoryForRecordOrdered(r, ordered);
    if (t ? sameId(r.territoryId, t.id) : r.territoryId === undefined) continue;
    out.push(patch.set('records', r.id, { territoryId: t ? t.id : undefined }));
  }
  return out;
}

/** `AppStore.records(in:)`: non-archived stores tagged with the territory or inside its polygon. */
export function recordsInTerritory<R extends RecordPayload>(t: TerritoryPayload, records: readonly R[]): R[] {
  return records.filter((r) => !r.isArchived && (sameId(r.territoryId, t.id) || territoryContains(t, r.coordinate)));
}

export interface TerritoryStats {
  records: number;
  /** Completed visits on those stores in the last 30 days. */
  visits30d: number;
  /** Stores with a `wonAt`. */
  won: number;
  /** Value of the stores whose stage is not a lost stage. */
  pipelineValue: number;
}

/** `AppStore.territoryStats(_:)`. */
export function territoryStats(
  t: TerritoryPayload,
  data: { records: readonly RecordPayload[]; activities: readonly ActivityPayload[]; pipelines: readonly PipelinePayload[] },
  now: Date = new Date(),
): TerritoryStats {
  const rs = recordsInTerritory(t, data.records);
  const ids = idSet(rs.map((r) => r.id));
  const since = addDays(now, -30).getTime();
  let visits30d = 0;
  for (const a of data.activities) {
    if (a.type === 'visit' && a.status === 'completed' && hasId(ids, a.recordId) && effectiveMs(a) >= since) visits30d += 1;
  }
  return {
    records: rs.length,
    visits30d,
    won: rs.filter((r) => r.wonAt !== undefined).length,
    pipelineValue: rs.filter((r) => stageFor(r, data.pipelines)?.isLost !== true).reduce((sum, r) => sum + r.value, 0),
  };
}

/** `Geo.areaSquareMeters` of the territory's polygon (0 for ZIP territories and shapes under 3 points). */
export function territoryArea(t: Pick<TerritoryPayload, 'polygon'>): number {
  return areaSquareMeters(t.polygon);
}

/** `TerritoryFormat.area`: "2.40 sq mi", "38 acres", "6.1 sq km", "12 ha" in the company distance unit. */
export function formatArea(squareMeters: number, unit: DistanceUnit): string {
  if (!(squareMeters > 0)) return `0 sq ${unit === 'miles' ? 'mi' : 'km'}`;
  if (unit === 'miles') {
    const sqMi = squareMeters / 2_589_988.11;
    if (sqMi < 0.1) {
      const acres = squareMeters / 4_046.856;
      return acres < 10 ? `${formatFixed(acres, 1)} acres` : `${Math.round(acres)} acres`;
    }
    return sqMi < 10 ? `${formatFixed(sqMi, 2)} sq mi` : `${formatFixed(sqMi, 1)} sq mi`;
  }
  const sqKm = squareMeters / 1_000_000;
  if (sqKm < 0.1) {
    const ha = squareMeters / 10_000;
    return ha < 10 ? `${formatFixed(ha, 1)} ha` : `${Math.round(ha)} ha`;
  }
  return sqKm < 10 ? `${formatFixed(sqKm, 2)} sq km` : `${formatFixed(sqKm, 1)} sq km`;
}

/** `TerritoryFormat.areaUnitName`. */
export function areaUnitName(unit: DistanceUnit): string {
  return unit === 'miles' ? 'sq mi' : 'sq km';
}

/**
 * Editor input for ZIP territories (WEB_PLAN.md §5.8): split on commas, semicolons and newlines, trimmed,
 * upper-cased, empty and repeated entries dropped.
 */
export function parsePostalCodes(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[,;\n\r]+/)) {
    const code = part.trim().toUpperCase();
    if (code !== '' && !out.includes(code)) out.push(code);
  }
  return out;
}

/**
 * Active polygon territories that overlap `t` (a vertex of one inside the other), for the editor's overlap warning.
 * Edge-only crossings without a contained vertex are not detected.
 */
export function overlappingTerritories(t: Pick<TerritoryPayload, 'id' | 'polygon'>, territories: readonly TerritoryPayload[]): TerritoryPayload[] {
  if (t.polygon.length < 3) return [];
  return territories.filter(
    (o) =>
      o.isActive &&
      !sameId(o.id, t.id) &&
      o.polygon.length >= 3 &&
      (o.polygon.some((p) => polygonContains(t.polygon, p)) || t.polygon.some((p) => polygonContains(o.polygon, p))),
  );
}
